import { httpClient } from './httpClient';
import type { ApiResponse } from './errors';
import { RULE_CODES } from './types/orderEdit';
import type {
  EditCredential,
  EditErrorCode,
  EditView,
  ModificationStatus,
  Quote,
  SubmitResult,
  Target,
} from './types/orderEdit';

// EAUG-77: the /api/order-edit endpoints. HttpClient never throws; a failed
// call resolves to { success: false, error: ApiError } whose `details` is the
// JSON error body ({ error: '<code>', … }).

const SERVER_CODES: ReadonlySet<string> = new Set<string>([
  'invalid_link',
  'disabled',
  'order_edit_unavailable',
  'deadline_passed',
  'not_eligible',
  'price_unavailable',
  'quote_changed',
  'payment_in_progress',
  'modification_pending',
  'headcount_changed',
  'too_many_requests',
  ...RULE_CODES,
]);

export interface EditApiError {
  /** HTTP status; null when no answer arrived. */
  status: number | null;
  code: EditErrorCode;
  /** The JSON error body (error, product, reason, quote, deadline); {} when none. */
  body: Record<string, unknown>;
}

/** The order-edit error behind a failed call. */
export function readEditError(res: ApiResponse<unknown>): EditApiError {
  const status = res.error?.status ?? res.status ?? null;
  const details: unknown = res.error?.details;
  const body = details !== null && typeof details === 'object' ? (details as Record<string, unknown>) : {};
  const raw = typeof body.error === 'string' ? body.error : '';
  let code: EditErrorCode;
  if (SERVER_CODES.has(raw)) code = raw as EditErrorCode;
  else if (status === 429) code = 'too_many_requests';
  else if (status === 503) code = 'order_edit_unavailable';
  else if (status === null) code = 'network';
  else code = 'unknown';
  return { status, code, body };
}

/** A read answer the page can render; anything else is treated as an error, never rendered. */
export function isEditView(data: unknown): data is EditView {
  if (data === null || typeof data !== 'object') return false;
  const view = data as Partial<EditView>;
  const sections = view.sections;
  return (
    (view.capability === 'full' || view.capability === 'drinks_add') &&
    typeof view.headcount === 'number' &&
    !!sections &&
    Array.isArray(sections.menus) &&
    Array.isArray(sections.drinks) &&
    Array.isArray(sections.options)
  );
}

function credentialFields(cred: EditCredential): { token: string } | { menuToken: string } {
  return 'token' in cred ? { token: cred.token } : { menuToken: cred.menuToken };
}

/** The booking's editable order: GET /api/order-edit?token= | ?menuToken= */
export async function getEditView(cred: EditCredential): Promise<ApiResponse<EditView>> {
  return httpClient.get<EditView>('api/order-edit', { params: credentialFields(cred) }, { retries: 1 });
}

/** E-mails the personal edit link to the booking's address (menu link only). */
export async function requestEditLink(menuToken: string): Promise<ApiResponse<{ sentTo: string }>> {
  // Never retried: each attempt can send an e-mail.
  return httpClient.post<{ sentTo: string }>('api/order-edit/link', { menuToken }, undefined, { retries: 0 });
}

/** Prices the changed lines (targets lists only changed products). */
export async function quoteEdit(cred: EditCredential, targets: Target[]): Promise<ApiResponse<Quote>> {
  return httpClient.post<Quote>('api/order-edit/quote', { ...credentialFields(cred), targets }, undefined, { retries: 1 });
}

/** Applies (net ≤ 0) or opens the Stripe payment (net > 0) for the quoted change. */
export async function submitEdit(
  cred: EditCredential,
  targets: Target[],
  expectedNetCents: number,
): Promise<ApiResponse<SubmitResult>> {
  // Never retried: a retry after a lost answer would expire the Stripe
  // session the first attempt opened (D8).
  return httpClient.post<SubmitResult>(
    'api/order-edit/submit',
    { ...credentialFields(cred), targets, expectedNetCents },
    undefined,
    { retries: 0 },
  );
}

/** Where a modification stands; answers even with the feature off (ungated). */
export async function getEditStatus(
  cred: EditCredential,
  id: number,
): Promise<ApiResponse<{ status: ModificationStatus }>> {
  return httpClient.get<{ status: ModificationStatus }>(
    'api/order-edit/status',
    { params: { ...credentialFields(cred), id } },
    { retries: 0 },
  );
}
